import { useState, useEffect, useRef, createContext, useContext, lazy, Suspense } from "react";
import { buildCartLine, refreshCartLine } from "./lib/productView";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
// Send the admin session cookie on every request, not just calls that remembered to opt in -
// several admin mutation endpoints (edit/delete product, edit/delete/update order) require it
// and previously had no way to receive it since none of their call sites passed
// withCredentials individually. Harmless for customer-facing requests: the browser only
// attaches the admin_token cookie if one exists, which it never does for a regular shopper.
axios.defaults.withCredentials = true;
import { Toaster, toast } from "sonner";
import { getSizePrice } from "./components/v2/constants";

// Independence Day offer exclusion helper - Exclusive Line and new-launch products don't
// count toward the discount tier threshold and don't receive the discount themselves.
export const getIndOfferExclusionLabel = (item) => {
  const excluded = item?.fragrance_family === 'Exclusive' || (item?.tags || []).includes('new');
  return excluded ? 'Offer is not applicable on selected item' : null;
};

// Components
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

// Contexts
import { WishlistProvider } from "./contexts/WishlistContext";

// Lazy load heavy pages for code splitting
const HomePageV2 = lazy(() => import("./pages/HomePageV2"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ProductDetailPageV2 = lazy(() => import("./pages/ProductDetailPageV2"));
const ProductsPageV2 = lazy(() => import("./pages/ProductsPageV2"));
const ThePerfumerPageV2 = lazy(() => import("./pages/ThePerfumerPageV2"));
const JournalPageV2 = lazy(() => import("./pages/JournalPageV2"));
const PolicyPageV2 = lazy(() => import("./pages/PolicyPageV2"));

// Admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminReferrals = lazy(() => import("./pages/admin/AdminReferrals"));
const AdminVouchers = lazy(() => import("./pages/admin/AdminVouchers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPolicies = lazy(() => import("./pages/admin/AdminPolicies"));
const AdminProductEdit = lazy(() => import("./pages/admin/AdminProductEdit"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminActivity = lazy(() => import("./pages/admin/AdminActivity"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminV2Content = lazy(() => import("./pages/AdminV2Content"));
const CartPageV2 = lazy(() => import("./pages/CartPageV2"));
const CheckoutPageV2 = lazy(() => import("./pages/CheckoutPageV2"));
const OrderConfirmationV2 = lazy(() => import("./pages/OrderConfirmationV2"));

// Loading fallback - V2 theme styling
const PageLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a0b0e',
    flexDirection: 'column',
    gap: '16px'
  }}>
    <div style={{
      width: '32px',
      height: '32px',
      border: '2px solid rgba(169, 200, 255, 0.2)',
      borderTopColor: '#a9c8ff',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <div style={{
      fontFamily: "'Lora', Georgia, serif",
      fontSize: '14px',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#9ba3b0'
    }}>Loading</div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { settings } = useSettings();
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("scent_cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [referralCredit, setReferralCredit] = useState(0);
  const [referralCode, setReferralCode] = useState(() => {
    return localStorage.getItem("scent_referral_code") || "";
  });

  useEffect(() => {
    localStorage.setItem("scent_cart", JSON.stringify(cart));
  }, [cart]);

  // A saved cart is only a list of (product, size, quantity). Name, image, price and tags are
  // re-read from the live catalogue once per visit, so the cart can never show stale details.
  const cartSynced = useRef(false);
  useEffect(() => {
    if (cartSynced.current || cart.length === 0) return;
    cartSynced.current = true;
    axios.get(`${API}/products`).then((r) => {
      const byId = Object.fromEntries(r.data.map((p) => [p.id, p]));
      setCart((prev) => {
        const next = prev.map((line) => refreshCartLine(line, byId[line.product_id]));
        return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
      });
    }).catch(() => {});
  }, [cart.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use centralized utility for price lookup
  const getPriceForSize = getSizePrice;

  const addToCart = (product, size, quantity = 1, isGiftBox = false) => {
    const price = getPriceForSize(product, size);
    // Prevent adding to cart if no valid price for this size
    if (!price) {
      toast.error(`${size} is not available for ${product.name}`);
      return;
    }
    
    setCart((prev) => {
      // Find existing item with same product_id, size, AND gift box status
      const existing = prev.find(
        (item) => item.product_id === product.id && item.size === size && (item.is_gift_box || false) === isGiftBox
      );
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.id && item.size === size && (item.is_gift_box || false) === isGiftBox
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [
        ...prev,
        buildCartLine(product, size, quantity, isGiftBox),
      ];
    });
    toast.success(`${product.name} added to cart`);
  };

  // Alternative addToCart that accepts a pre-built cart item object
  // Used by QuickViewModal which passes {product_id, product_name, size, quantity, unit_price, image, is_gift_box}
  const addItemToCart = (item) => {
    if (!item || !item.product_id || !item.size) {
      return;
    }
    
    // Validate that unit_price is a positive number - prevent adding unpriced items
    if (!item.unit_price || typeof item.unit_price !== 'number' || item.unit_price <= 0) {
      toast.error(`${item.size} is not available for this product`);
      return;
    }
    
    const isGiftBox = item.is_gift_box || false;
    
    setCart((prev) => {
      const existing = prev.find(
        (cartItem) => cartItem.product_id === item.product_id && cartItem.size === item.size && (cartItem.is_gift_box || false) === isGiftBox
      );
      if (existing) {
        return prev.map((cartItem) =>
          cartItem.product_id === item.product_id && cartItem.size === item.size && (cartItem.is_gift_box || false) === isGiftBox
            ? { ...cartItem, quantity: cartItem.quantity + (item.quantity || 1) }
            : cartItem
        );
      }
      return [
        ...prev,
        {
          product_id: item.product_id,
          product_name: item.product_name,
          size: item.size,
          quantity: item.quantity || 1,
          unit_price: item.unit_price,
          image: item.image || "",
          is_gift_box: isGiftBox,
          fragrance_family: item.fragrance_family || "",
          tags: item.tags || [],
        },
      ];
    });
  };

  const updateQuantity = (productId, size, quantity, isGiftBox = false) => {
    if (quantity <= 0) {
      removeFromCart(productId, size, isGiftBox);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.size === size && (item.is_gift_box || false) === isGiftBox
          ? { ...item, quantity }
          : item
      )
    );
  };

  const removeFromCart = (productId, size, isGiftBox = false) => {
    setCart((prev) =>
      prev.filter(
        (item) => !(item.product_id === productId && item.size === size && (item.is_gift_box || false) === isGiftBox)
      )
    );
    toast.success("Item removed from cart");
  };

  // Toggle gift box for a cart item (only for 50ml and 100ml)
  // When toggling ON, it modifies the current item to add gift box
  // When toggling OFF, it modifies the current item to remove gift box
  const toggleGiftBox = (productId, size, currentIsGiftBox = false) => {
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.size === size && (item.is_gift_box || false) === currentIsGiftBox
          ? { ...item, is_gift_box: !item.is_gift_box, gift_box_qty: !item.is_gift_box ? item.quantity : 0 }
          : item
      )
    );
  };

  // Update gift box quantity for a specific cart item (for per-unit gift box selection)
  const updateGiftBoxQty = (productId, size, giftBoxQty) => {
    setCart((prev) =>
      prev.map((item) =>
        item.product_id === productId && item.size === size
          ? { 
              ...item, 
              gift_box_qty: Math.max(0, Math.min(giftBoxQty, item.quantity)),
              is_gift_box: giftBoxQty > 0
            }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("scent_cart");
  };

  const applyReferral = async (code) => {
    try {
      const response = await axios.get(`${API}/referrals/by-code/${code}`);
      if (response.data.valid) {
        setReferralCode(code);
        setReferralCredit(response.data.discount_percent || 10); // Now stores percentage
        localStorage.setItem("scent_referral_code", code);
        toast.success(`10% discount applied!`);
        return true;
      }
    } catch {
      toast.error("Invalid referral code");
    }
    return false;
  };

  const subtotal = cart.reduce(
    (sum, item) => sum + item.unit_price * item.quantity,
    0
  );
  
  // Gift box calculation - now uses gift_box_qty for per-unit selection
  const giftBoxPrice = parseInt(settings.gift_box_price) || 300;
  const giftBoxEnabled = settings.gift_box_enabled === "true";
  const giftBoxTotal = giftBoxEnabled ? cart.reduce(
    (sum, item) => {
      const isEligible = item.size?.toLowerCase() === "50ml" || item.size?.toLowerCase() === "100ml";
      const giftQty = item.gift_box_qty || (item.is_gift_box ? item.quantity : 0); // backward compat
      return sum + (isEligible ? giftBoxPrice * giftQty : 0);
    },
    0
  ) : 0;
  
  // Use dynamic settings for shipping calculation - the actual shippingCharge value
  // is computed further below, after the Independence Day discount, since free
  // shipping eligibility during that offer depends on the post-discount amount.
  const flatShippingRate = parseInt(settings.flat_shipping_rate) || 100;
  
  // GST calculation (GST-inclusive pricing - prices already include GST)
  const gstPercent = parseFloat(settings.gst_percent) || 18;
  const gstMultiplier = gstPercent / (100 + gstPercent); // For 18%, this is 18/118
  const gstAmount = Math.round(subtotal * gstMultiplier);
  const baseAmount = subtotal - gstAmount; // Pre-GST amount
  
  // Independence Day Offer (time-limited)
  const IND_OFFER_START = '2026-08-15T00:00:00+05:30';
  const IND_OFFER_END = '2026-08-17T10:00:00+05:30';
  const indOfferActive = Date.now() >= new Date(IND_OFFER_START).getTime() && Date.now() < new Date(IND_OFFER_END).getTime();
  const indOfferTiers = [{ threshold: 7900, percent: 17.5 }, { threshold: 1500, percent: 15 }];
  // Independence Day offer excludes Exclusive Line and new-launch products - their value
  // doesn't count toward reaching a discount tier and they don't receive the discount.
  const isIndOfferExcluded = (item) => item.fragrance_family === 'Exclusive' || (item.tags || []).includes('new');
  const indOfferEligibleSubtotal = cart.reduce(
    (sum, item) => sum + (isIndOfferExcluded(item) ? 0 : item.unit_price * item.quantity),
    0
  );
  const indOfferTier = indOfferActive ? indOfferTiers.find(t => indOfferEligibleSubtotal >= t.threshold) : null;
  const autoDiscountThreshold = indOfferTier ? indOfferTier.threshold : (parseInt(settings.auto_discount_threshold) || 0);
  const autoDiscountPercent = indOfferTier ? indOfferTier.percent : (parseInt(settings.auto_discount_percent) || 0);
  const autoDiscountEligible = indOfferTier ? true : (autoDiscountThreshold > 0 && subtotal >= autoDiscountThreshold);
  const autoDiscount = autoDiscountEligible ? Math.round((indOfferTier ? indOfferEligibleSubtotal : subtotal) * (autoDiscountPercent / 100)) : 0;
  const autoDiscountLabel = indOfferTier ? 'Independence Day Offer' : 'Bulk discount';
  // Free shipping threshold - during the Independence Day offer, eligibility is based on
  // the POST-discount amount (what the customer actually pays before shipping), not the
  // raw subtotal, so a ₹2500 order that drops to ~₹2125 after 15% off needs to clear
  // ₹2500 net, not just gross. Outside the offer, falls back to the normal admin setting.
  const INDEPENDENCE_DAY_FREE_SHIPPING_THRESHOLD = 2000;
  const freeShippingThreshold = indOfferTier ? INDEPENDENCE_DAY_FREE_SHIPPING_THRESHOLD : (parseInt(settings.free_shipping_threshold) || 2000);
  const shippingEligibleAmount = indOfferTier ? (subtotal - autoDiscount) : subtotal;
  const shippingCharge = shippingEligibleAmount >= freeShippingThreshold ? 0 : flatShippingRate;
  // Calculate referral discount as percentage of subtotal
  const referralDiscount = referralCode ? Math.round(subtotal * (referralCredit / 100)) : 0;
  
  // Total = subtotal + gift box + shipping - auto discount - referral discount
  const totalAmount = subtotal + giftBoxTotal + shippingCharge - autoDiscount - referralDiscount;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        addItemToCart,
        updateQuantity,
        updateCartItemQuantity: updateQuantity,
        removeFromCart,
        toggleGiftBox,
        updateGiftBoxQty,
        clearCart,
        subtotal,
        baseAmount,
        gstAmount,
        gstPercent,
        giftBoxPrice,
        giftBoxEnabled,
        giftBoxTotal,
        shippingCharge,
        freeShippingThreshold,
        autoDiscountThreshold,
        autoDiscountPercent,
        autoDiscountEligible,
        autoDiscount,
        autoDiscountLabel,
        indOfferActive,
        referralCredit, // percentage
        referralDiscount, // actual discount amount
        referralCode,
        applyReferral,
        totalAmount,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Settings Context
const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [ga4Loaded, setGa4Loaded] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      // Settings fetch failed - use defaults
    } finally {
      setLoading(false);
    }
  };

  // Load GA4 script when measurement ID is available
  useEffect(() => {
    const ga4Id = settings.ga4_measurement_id;
    if (ga4Id && ga4Id.startsWith("G-") && !ga4Loaded) {
      // Add gtag script
      const gtagScript = document.createElement("script");
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
      document.head.appendChild(gtagScript);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", ga4Id);

      setGa4Loaded(true);
      
    }
  }, [settings.ga4_measurement_id, ga4Loaded]);

  useEffect(() => {
    fetchSettings();
    // Poll for settings updates every 5 seconds for real-time sync
    const interval = setInterval(fetchSettings, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loading, fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-cream">
      <ErrorBoundary>
        <SettingsProvider>
          <CartProvider>
            <WishlistProvider>
              <BrowserRouter>
                <Toaster 
                  position="top-center" 
                  toastOptions={{
                    style: {
                      background: '#1A1A1A',
                      color: '#FAF8F3',
                      border: '1px solid #D4AF37'
                    }
                  }}
                />
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                  {/* V2 Routes - Now Default */}
                  <Route path="/" element={<HomePageV2 />} />
                  <Route path="/shop" element={<ProductsPageV2 />} />
                  <Route path="/products/:slug" element={<ProductDetailPageV2 />} />
                  <Route path="/the-perfumer" element={<ThePerfumerPageV2 />} />
                  <Route path="/journal" element={<JournalPageV2 />} />
                  <Route path="/cart" element={<CartPageV2 />} />
                  <Route path="/checkout" element={<CheckoutPageV2 />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmationV2 />} />
                  <Route path="/shipping-policy" element={<PolicyPageV2 slug="shipping-policy" />} />
                  <Route path="/returns-policy" element={<PolicyPageV2 slug="returns-policy" />} />
                  <Route path="/privacy-policy" element={<PolicyPageV2 slug="privacy-policy" />} />
                  <Route path="/terms" element={<PolicyPageV2 slug="terms" />} />
                  
                  {/* V2 routes also accessible at /v2 for backward compatibility */}
                  <Route path="/v2" element={<HomePageV2 />} />
                  <Route path="/v2/shop" element={<ProductsPageV2 />} />
                  <Route path="/v2/products/:slug" element={<ProductDetailPageV2 />} />
                  <Route path="/v2/the-perfumer" element={<ThePerfumerPageV2 />} />
                  <Route path="/v2/journal" element={<JournalPageV2 />} />
                  <Route path="/v2/cart" element={<CartPageV2 />} />
                  <Route path="/v2/checkout" element={<CheckoutPageV2 />} />
                  <Route path="/v2/order-confirmation/:orderId" element={<OrderConfirmationV2 />} />
                  <Route path="/v2/shipping-policy" element={<PolicyPageV2 slug="shipping-policy" />} />
                  <Route path="/v2/returns-policy" element={<PolicyPageV2 slug="returns-policy" />} />
                  <Route path="/v2/privacy-policy" element={<PolicyPageV2 slug="privacy-policy" />} />
                  <Route path="/v2/terms" element={<PolicyPageV2 slug="terms" />} />
                  
                  {/* Shared Routes */}
                  <Route path="/product/:slug" element={<ProductDetailPageV2 />} />
                  <Route path="/our-story" element={<PolicyPageV2 slug="our-story" />} />
                  <Route path="/contact" element={<PolicyPageV2 slug="contact" />} />
                  <Route path="/policy/:slug" element={<PolicyPageV2 />} />
                  <Route path="/cancellation-policy" element={<PolicyPageV2 slug="cancellation-policy" />} />
                  <Route path="/r/:code" element={<Navigate to="/" replace />} />
                  <Route path="/referral" element={<Navigate to="/" replace />} />
                  <Route path="/referrals" element={<Navigate to="/" replace />} />
                  <Route path="/my-referrals" element={<Navigate to="/" replace />} />
                  
                  {/* Admin Login - Public */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                  
                  {/* Admin Routes - Protected */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="activity" element={<AdminActivity />} />
                    <Route path="products/new" element={<AdminProductEdit />} />
                    <Route path="products/:id" element={<AdminProductEdit />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="orders/:id" element={<AdminOrderDetail />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="referrals" element={<AdminReferrals />} />
                    <Route path="vouchers" element={<AdminVouchers />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="policies" element={<AdminPolicies />} />
                    <Route path="v2-content" element={<AdminV2Content />} />
                  </Route>
                  
                  {/* 404 Catch-all */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </SettingsProvider>
    </ErrorBoundary>
  </div>
);
}

export default App;
